/** @jsxImportSource @emotion/react */
import { Box, Button, Chip, Collapse, MenuItem, Select, Typography } from '@mui/material'
import { useAtom } from 'jotai'
import { useMemo, useState } from 'react'
import { scenarioFiltersAtomFamily } from '../state/hazard.atoms'
import { HazardScenarioFiltersProps, ScenarioParameterKey } from '../types/hazard.types'
import {
  formatFilterValue,
  resolveFilterValuesByKey,
  ScenarioFilterValue,
} from '../utils/hazardScenarioFilters.utils'
import { formatParamLabel } from '../utils/hazardScenarioGrid.utils'
import {
  activeCountStyles,
  activeCountStylesContainer,
  bodyStyles,
  chipsRowStyles,
  containerStyles,
  filterGroupStyles,
  filterLabelStyles,
  footerRowStyles,
  headerLabelStyles,
  headerStyles,
  optionChipStyles,
  resetButtonStyles,
  toggleButtonStyles,
} from './HazardScenarioFilters.styles'

const CHIP_THRESHOLD = 3

type FilterChipsProps = {
  values: ScenarioFilterValue[]
  activeValue: ScenarioFilterValue | undefined
  onSelect: (value: ScenarioFilterValue) => void
}

const FilterChips = ({ values, activeValue, onSelect }: FilterChipsProps) => (
  <Box css={chipsRowStyles}>
    {values.map((value) => (
      <Chip
        key={String(value)}
        label={formatFilterValue(value)}
        onClick={() => onSelect(value)}
        clickable
        css={optionChipStyles(activeValue === value)}
      />
    ))}
  </Box>
)

type FilterSelectProps = {
  paramKey: string
  values: ScenarioFilterValue[]
  activeValue: ScenarioFilterValue | undefined
  onSelect: (value: ScenarioFilterValue | undefined) => void
}

const FilterSelect = ({ paramKey, values, activeValue, onSelect }: FilterSelectProps) => (
  <Select
    size="small"
    displayEmpty
    value={activeValue ?? ''}
    onChange={(e) => {
      const val = e.target.value
      onSelect(val === '' ? undefined : (val as ScenarioFilterValue))
    }}
    inputProps={{ 'aria-label': formatParamLabel(paramKey) }}
    sx={{
      fontSize: '0.8rem',
      minWidth: 160,
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
    }}
  >
    <MenuItem value="">
      <Typography variant="caption" color="text.secondary">
        All
      </Typography>
    </MenuItem>
    {values.map((value) => (
      <MenuItem key={String(value)} value={value as string | number}>
        {formatFilterValue(value)}
      </MenuItem>
    ))}
  </Select>
)

// ── Main component ────────────────────────────────────────────────────────────

export const HazardScenarioFilters = ({
  layerId,
  scenarios,
  filterableParams,
}: HazardScenarioFiltersProps) => {
  const [activeFilters, setActiveFilters] = useAtom(scenarioFiltersAtomFamily(layerId))
  const [isOpen, setIsOpen] = useState(true)

  const paramValuesMap = useMemo(
    () => resolveFilterValuesByKey(scenarios, filterableParams),
    [scenarios, filterableParams],
  )

  const activeCount = filterableParams.filter((key) => activeFilters[key] !== undefined).length

  if (filterableParams.length === 0) return null

  const handleChipClick = (key: ScenarioParameterKey, value: ScenarioFilterValue) => {
    setActiveFilters((prev) => {
      if (prev[key] === value) {
        const next = { ...prev }
        delete next[key]
        return next
      }
      return { ...prev, [key]: value }
    })
  }

  const handleSelectChange = (
    key: ScenarioParameterKey,
    value: ScenarioFilterValue | undefined,
  ) => {
    setActiveFilters((prev) => {
      if (value === undefined) {
        const next = { ...prev }
        delete next[key]
        return next
      }
      return { ...prev, [key]: value }
    })
  }

  return (
    <Box css={containerStyles}>
      <Box css={headerStyles}>
        <Typography css={headerLabelStyles}>Filters</Typography>

        <Box css={activeCountStylesContainer}>
          {activeCount > 0 && <Box css={activeCountStyles}>{activeCount}</Box>}

          <Button
            onClick={() => setIsOpen((v) => !v)}
            aria-expanded={isOpen}
            aria-controls="hazard-scenario-filters-body"
            disableElevation
            disableRipple
            css={toggleButtonStyles}
            size="small"
          >
            {isOpen ? 'Hide' : 'Show'}
          </Button>
        </Box>
      </Box>

      <Collapse in={isOpen}>
        <Box id="hazard-scenario-filters-body" css={bodyStyles}>
          {filterableParams.map((key) => {
            const values = paramValuesMap[key] ?? []
            const activeValue = activeFilters[key]
            const useDropdown = values.length > CHIP_THRESHOLD

            return (
              <Box key={key} css={filterGroupStyles}>
                <Typography css={filterLabelStyles}>{formatParamLabel(key)}</Typography>

                {useDropdown ? (
                  <FilterSelect
                    paramKey={key}
                    values={values}
                    activeValue={activeValue}
                    onSelect={(value) => handleSelectChange(key, value)}
                  />
                ) : (
                  <FilterChips
                    values={values}
                    activeValue={activeValue}
                    onSelect={(value) => handleChipClick(key, value)}
                  />
                )}
              </Box>
            )
          })}

          {activeCount > 0 && (
            <Box css={footerRowStyles}>
              <Button
                size="small"
                variant="text"
                onClick={() => setActiveFilters({})}
                css={resetButtonStyles}
              >
                Reset all filters
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}
